import { DropdownChangeParams } from 'primereact/dropdown'
import { useCallback, useEffect, useReducer, useState } from 'react'
import { useTranslation } from 'react-i18next'

// local imports
import DashboardService from 'service/DashboardService'
import { IAllFirmList } from 'types/FirmType'
import {
  ChartItems,
  IOnCampusHealthServiceAllFirmsStatistics,
  IOnCampusHealthServiceFirmStatistics,
} from 'types/DashboardType'
import DateUtil from 'utils/DateUtil'
import { roundPercentages } from 'utils/Miscellaneous'

interface IChartState {
  colors: string[]
  items: ChartItems | undefined
  labels: string[]
  footerTitle: string
  detailTitle: string
}

const chartColors = ['#465393', '#7986C6', '#CDD2EA']

/* Reducer */
const initialState: IChartState = {
  items: undefined,
  colors: chartColors,
  labels: ['dashboard.on-campus-tabs.came', 'dashboard.on-campus-tabs.not_came', 'dashboard.on-campus-tabs.cancelled'],
  footerTitle: 'dashboard.on-campus-tabs.all-footer-msg',
  detailTitle: 'dashboard.on-campus-tabs.all-detail-msg',
}

function chartReducer(state: IChartState = initialState, action: { type: string; payload: { items: ChartItems } }) {
  const { type, payload } = action
  switch (type) {
    case 'all':
      return { ...initialState, ...payload }
    case 'came':
      return {
        ...state,
        items: payload.items,
        colors: [chartColors[0]],
        labels: ['dashboard.on-campus-tabs.came'],
        detailTitle: 'dashboard.on-campus-tabs.came-detail-msg',
        footerTitle: 'dashboard.on-campus-tabs.came-footer-msg',
      }
    case 'not_came':
      return {
        ...state,
        items: payload.items,
        colors: [chartColors[1]],
        labels: ['dashboard.on-campus-tabs.not_came'],
        detailTitle: 'dashboard.on-campus-tabs.not-came-detail-msg',
        footerTitle: 'dashboard.on-campus-tabs.not-came-footer-msg',
      }
    case 'cancelled':
      return {
        ...state,
        items: payload.items,
        colors: [chartColors[2]],
        labels: ['dashboard.on-campus-tabs.cancelled'],
        detailTitle: 'dashboard.on-campus-tabs.canceled-detail-msg',
        footerTitle: 'dashboard.on-campus-tabs.canceled-footer-msg',
      }
    default:
      return initialState
  }
}

const fetchSumOfKey = (arr: any[], property: string): number => {
  return arr.map((item: any) => item[property]).reduce((prev: any, curr: any) => prev + curr, 0)
}

const percentage = (partialValue: number, total: number) => (100 * partialValue) / total

const useGetHealthServiceChartData = (props: any) => {
  const { t } = useTranslation()

  const chartTypeOptions = [
    { key: 'all', value: t('dashboard.on-campus-tabs.all-type') },
    { key: 'came', value: t('dashboard.on-campus-tabs.incoming-type') },
    { key: 'not_came', value: t('dashboard.on-campus-tabs.not-coming-type') },
    { key: 'cancelled', value: t('dashboard.on-campus-tabs.cancelled-type') },
  ]

  const [{ items, colors, labels, detailTitle, footerTitle }, dispatch] = useReducer(chartReducer, initialState)
  const [chartData, setChartData] = useState<IOnCampusHealthServiceAllFirmsStatistics | undefined>()
  const [chartType, setChartType] = useState('all')
  const [loading, setLoading] = useState(false)
  const [excelData, setExcelData] = useState<any>()

  const chooseChartDataWithType = (
    selectedType: string,
    data: IOnCampusHealthServiceAllFirmsStatistics | undefined
  ) => {
    if (!data) return

    const cameCount = data.firmStatistics.map((i: IOnCampusHealthServiceFirmStatistics) => i.cameCount)
    const notCameCount = data.firmStatistics.map((i: IOnCampusHealthServiceFirmStatistics) => i.notCameCount)
    const cancelCount = data.firmStatistics.map((i: IOnCampusHealthServiceFirmStatistics) => i.cancelCount)
    const camePercent = data.firmStatistics.map((i: IOnCampusHealthServiceFirmStatistics) => {
      return percentage(i.cameCount, i.totalCount)
    })
    const notCamePercent = data.firmStatistics.map((i: IOnCampusHealthServiceFirmStatistics) => {
      return percentage(i.notCameCount, i.totalCount)
    })
    const cancelPercent = data.firmStatistics.map((i: IOnCampusHealthServiceFirmStatistics) => {
      return percentage(i.cancelCount, i.totalCount)
    })

    if (selectedType === 'all') {
      const payload = {
        items: {
          doughnutItems: {
            datasets: [
              fetchSumOfKey(data.firmStatistics, 'cameCount'),
              fetchSumOfKey(data.firmStatistics, 'notCameCount'),
              fetchSumOfKey(data.firmStatistics, 'cancelCount'),
            ],
            total: fetchSumOfKey(data.firmStatistics, 'totalCount'),
            totalPercentage: 0,
          },
          stackedItems: {
            labels: data.firmStatistics.map((item: IOnCampusHealthServiceFirmStatistics) => item.firmName),
            count: [cameCount, notCameCount, cancelCount],
            percentage: [
              roundPercentages(camePercent),
              roundPercentages(notCamePercent),
              roundPercentages(cancelPercent),
            ],
          },
        },
      }
      dispatch({ type: 'all', payload })
    } else if (selectedType === 'came') {
      const payload = {
        items: {
          doughnutItems: {
            datasets: [fetchSumOfKey(data.firmStatistics, 'cameCount')],
            total: fetchSumOfKey(data.firmStatistics, 'cameCount'),
            totalPercentage: 0,
          },
          stackedItems: {
            labels: data.firmStatistics.map((item: IOnCampusHealthServiceFirmStatistics) => item.firmName),
            count: [cameCount],
            percentage: [roundPercentages(camePercent)],
          },
        },
      }
      dispatch({ type: 'came', payload })
    } else if (selectedType === 'not_came') {
      const payload = {
        items: {
          doughnutItems: {
            datasets: [fetchSumOfKey(data.firmStatistics, 'notCameCount')],
            total: fetchSumOfKey(data.firmStatistics, 'notCameCount'),
            totalPercentage: 0,
          },
          stackedItems: {
            labels: data.firmStatistics.map((item: IOnCampusHealthServiceFirmStatistics) => item.firmName),
            count: [notCameCount],
            percentage: [roundPercentages(notCamePercent)],
          },
        },
      }
      dispatch({ type: 'not_came', payload })
    } else {
      const payload = {
        items: {
          doughnutItems: {
            datasets: [fetchSumOfKey(data.firmStatistics, 'cancelCount')],
            total: fetchSumOfKey(data.firmStatistics, 'cancelCount'),
            totalPercentage: 0,
          },
          stackedItems: {
            labels: data.firmStatistics.map((item: IOnCampusHealthServiceFirmStatistics) => item.firmName),
            count: [cancelCount],
            percentage: [roundPercentages(cancelPercent)],
          },
        },
      }
      dispatch({ type: 'cancelled', payload })
    }
  }

  const getChartData = useCallback(() => {
    let idList = props.selectedFirms.map((item: IAllFirmList) => item.id)
    const request = {
      firmIds: idList,
      startDate: DateUtil.formatDateToString(props.startDate),
      endDate: DateUtil.formatDateToString(props.endDate),
    }
    setLoading(true)

    DashboardService.getCampusHealthServiceStatistics(request)
      .then((resp) => {
        const array = resp.data.firmStatistics.filter((f) => idList.includes(f.firmId))
        chooseChartDataWithType(chartType, { ...resp.data, firmStatistics: array })
        setChartData({ ...resp.data, firmStatistics: array })
        setExcelData(array)
        setLoading(false)
      })
      .catch((err) => console.log(err))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.endDate, props.startDate, props.selectedFirms])

  /* Fetch Statistics */
  useEffect(() => getChartData(), [getChartData])

  /* Chart Type select box handler */
  const handleChartTypeChange = (e: DropdownChangeParams) => {
    const selectedType = e.value
    setChartType(selectedType)
    chooseChartDataWithType(selectedType, chartData)
  }

  return {
    t,
    excelData,
    chartType,
    chartTypeOptions,
    handleChartTypeChange,
    loading,
    items,
    colors,
    labels,
    detailTitle,
    footerTitle,
  }
}

export default useGetHealthServiceChartData
